<template>
  <div>
    <h1 class="text-primary md:font-normal font-sans md:text-mid text-h5 leading-none md:italic md:font-serif md:tracking-tiny mb-5 uppercase text-center md:text-left md:normal-case font-bold tracking-lg">
      {{ $t('Order History') }}
    </h1>
    <div class="bg-white lg:py-xl lg:px-8.5 p-md">
      <div class="lg:flex mb-sm justify-between">
        <div v-if="orders.length" class="mb-md lg:mb-0">
          <span>{{ `Items ${from}` }}</span>
          <span>{{ ` to ${to}` }}</span>
        </div>
        <div class="lg:flex items-center">
          <span class="mr-sm text-h4 leading-base tracking-mid hidden lg:inline">{{ $t('Show') }}</span>
          <base-select
            name="showBy"
            :options="showBy"
            input-class="uppercase order-pagination__select"
            caret-size="icon-xs"
            :selected="String(perPage)"
            v-model="perPage"
          />
          <span class="ml-sm text-h4 leading-base tracking-mid hidden lg:inline">{{ $t('per page') }}</span>
        </div>
      </div>

      <div class="flex row items-center leading-none py-4 px-2 justify-center mb-md">
        <a
          href="#"
          class="mr-sm no-underline text-grey-32 hover:text-black"
          :class="{ 'opacity-25': currentPage === 1 }"
          @click.prevent="currentPage === 1 ? null : prevPage()"
        >
          <svg class="icon-lg text-black fill-current">
            <use xlink:href="/assets/icons.svg#full-arrow-left" />
          </svg>
          <span class="inline-block text-black text-md mr-md">{{ $t('Newer') }}</span>
        </a>
        <span class="inline-block text-black mr-sm text-md">{{ currentPage }}</span>
        <a
          href="#"
          class="no-underline"
          :class="{ 'opacity-25': !hasMoreOrders }"
          @click.prevent="!hasMoreOrders ? null : nextPage()"
        >
          <span class="inline-block text-black mr-sm ml-md text-md">{{ $t('Older') }}</span>
          <svg class="icon-lg text-black fill-current">
            <use xlink:href="/assets/icons.svg#full-arrow-right" />
          </svg>
        </a>
      </div>

      <table class="w-full my-10" :class="{ loadingOrders: isLoading }">
        <thead>
          <tr>
            <th class="text-left font-normal text-h5 leading-none uppercase tracking-mid border-b border-dark border-solid pb-sm">
              {{ $t('ORDER #') }}
            </th>
            <th class="text-left font-normal text-h5 leading-none uppercase tracking-mid border-b border-dark border-solid pb-sm hidden sm:table-cell">
              {{ $t('DATE') }}
            </th>
            <th class="text-left font-normal text-h5 leading-none uppercase tracking-mid border-b border-dark border-solid pb-sm hidden md:table-cell">
              {{ $t('TYPE') }}
            </th>
            <th class="text-right font-normal text-h5 leading-none uppercase tracking-mid border-b border-dark border-solid pb-sm hidden md:table-cell">
              {{ $t('ORDER TOTAL') }}
            </th>
          </tr>
        </thead>
        <tbody v-if="!isLoading && !orders.length">
          <tr>
            <td class="w-1/2 py-2" colspan="4">
              <p class="leading-base tracking-mid mb-md my-3">
                {{ $t('Looks like you haven\'t placed any orders.') }}
              </p>
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr
            v-for="order in orders"
            :key="order.id"
          >
            <td class="pt-sm">
              <router-link
                :to="localizedRoute({ name: 'netsuite-order', params: { transactionId: order.id } })"
                class="pt-sm text-base leading-sm tracking-xs text-dark underline hover:text-primary2 slow-color-change"
              >
                {{ order.etail_id || order.id }}
              </router-link>
            </td>
            <td class="pt-sm hidden sm:table-cell">
              <router-link
                :to="localizedRoute({ name: 'netsuite-order', params: { transactionId: order.id } })"
                class="pt-sm text-base leading-sm tracking-xs text-dark underline hover:text-primary2 slow-color-change"
              >
                {{ order.order_date | localeDate }}
              </router-link>
            </td>
            <td class="pt-sm hidden md:table-cell">
              <router-link
                :to="localizedRoute({ name: 'netsuite-order', params: { transactionId: order.id } })"
                class="pt-sm text-base leading-sm tracking-xs text-dark underline hover:text-primary2 slow-color-change"
              >
                {{ formatOrderType(order.transaction_type) }}
              </router-link>
            </td>
            <td class="pt-sm text-right hidden md:table-cell">
              <router-link
                :to="localizedRoute({ name: 'netsuite-order', params: { transactionId: order.id } })"
                class="text-base leading-sm tracking-xs text-dark italic font-serif underline hover:text-primary2 slow-color-change"
              >
                {{ Number(order.order_total || 0) | price }}
              </router-link>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import debounce from 'lodash-es/debounce'
import BaseSelect from 'theme/components/core/blocks/Form/BaseSelect'
import { localeDate } from 'theme/filters/locale-date'

export default {
  name: 'NetsuiteOrders',
  components: {
    BaseSelect
  },
  filters: {
    localeDate
  },
  data () {
    return {
      currentPage: 1,
      perPage: 25,
      showBy: [
        { label: '25', value: '25' },
        { label: '50', value: '50' },
        { label: '100', value: '100' }
      ]
    }
  },
  computed: {
    ...mapGetters({
      orders: 'netsuite/getOrderSummaries',
      pagination: 'netsuite/getOrderSummariesPagination',
      isLoading: 'netsuite/isLoading'
    }),
    from () {
      if (!this.orders.length) return 0
      return ((this.currentPage - 1) * Number(this.perPage)) + 1
    },
    to () {
      if (!this.orders.length) return 0
      return ((this.currentPage - 1) * Number(this.perPage)) + this.orders.length
    },
    hasMoreOrders () {
      return this.orders.length >= Number(this.perPage)
    }
  },
  watch: {
    perPage () {
      this.currentPage = 1
      this.fetchPage()
    }
  },
  mounted () {
    this.fetchPage()
  },
  methods: {
    fetchPage: debounce(function () {
      this.$store.dispatch('netsuite/fetchOrderSummaries', {
        limit: Number(this.perPage),
        page: this.currentPage,
        orderBy: 'trandate',
        orderByDirection: 'DESC'
      }).catch(() => {})
    }, 250),
    prevPage () {
      if (this.currentPage <= 1) return
      this.currentPage -= 1
      this.fetchPage()
    },
    nextPage () {
      if (!this.hasMoreOrders) return
      this.currentPage += 1
      this.fetchPage()
    },
    formatOrderType (type) {
      if (!type) return ''
      const labels = {
        CustInvc: 'Invoice',
        SalesReceipt: 'Sales Receipt',
        CashSale: 'Sales Receipt',
        Journal: 'Journal',
        CustPymt: 'Payment',
        CustCred: 'Credit Memo',
        CashRfnd: 'Cash Refund',
        Deposit: 'Deposit',
        SalesOrd: 'Sales Order'
      }
      return labels[type] || type
    }
  }
}
</script>

<style lang="scss" scoped>
.loadingOrders {
  background-color: #bbbbbb;
  opacity: 0.5;
}

.order-pagination__select {
  @apply bg-grey-27 border-grey-21 normal-case;
  @screen lg {
    @apply py-1 bg-white;
  }
}
</style>
